import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { toast } from "sonner";
import { Database, Plug, Unplug, RefreshCcw } from "lucide-react";

export default function ProjectsPage() {
  const [busyName, setBusyName] = useState(null);
  const { data, loading, refetch } = useApiQuery(["projects"], "/projects");
  const projects = data?.projects || [];
  const serverError = data?.server_error;
  const load = () => refetch();

  const connect = async (name) => {
    setBusyName(name);
    try {
      const { data } = await api.post(`/projects/${encodeURIComponent(name)}/connect`);
      toast.success(`Connected '${name}'`, { description: data.message });
      load();
    } catch (e) {
      toast.error("Connection failed", { description: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
    } finally {
      setBusyName(null);
    }
  };

  const disconnect = async (name) => {
    if (!window.confirm(`Disconnect '${name}'? Its sites and points will be removed from the portal.`)) return;
    setBusyName(name);
    try {
      const { data } = await api.post(`/projects/${encodeURIComponent(name)}/disconnect`);
      toast.success(`Disconnected '${name}'`, { description: `${data.sites_removed} sites removed` });
      load();
    } catch (e) {
      toast.error("Disconnect failed", { description: formatApiErrorDetail(e?.response?.data?.detail) || e.message });
    } finally {
      setBusyName(null);
    }
  };

  return (
    <div className="max-w-5xl fade-up">
      <div className="k-label flex items-center gap-2">
        <Database className="h-3.5 w-3.5" /> Integrations
      </div>
      <div className="mt-2 flex items-end justify-between flex-wrap gap-4">
        <h1 className="text-4xl sm:text-5xl font-bold font-display text-[#212121]">Data Sources</h1>
        <button onClick={load} disabled={loading} className="k-btn-outline" data-testid="projects-refresh-btn">
          <RefreshCcw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <p className="mt-3 text-[#4D4D4D] text-sm max-w-2xl">
        Projects available on your data server. Connect a project to sync its sites and points into the portal —
        data refreshes automatically every hour.
      </p>

      {serverError && (
        <div className="mt-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="projects-server-error">
          Could not list projects from the server (host-level access may be required). Showing connected projects only.
        </div>
      )}

      <div className="mt-8 space-y-3" data-testid="projects-list">
        {loading ? (
          <div className="k-sublabel">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="k-card p-8 text-sm text-[#6B7280]" data-testid="projects-empty">No projects found.</div>
        ) : (
          projects.map((p, i) => (
            <div key={p.name} className="k-card p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`project-card-${i}`}>
              <div className="flex items-center gap-4">
                <div className={`h-10 w-10 rounded-md flex items-center justify-center ${p.connected ? "bg-[#156F5B]/10 text-[#156F5B]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
                  <Database className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-heading font-semibold text-[#212121]">{p.dis}</span>
                    <span className="text-xs text-[#9CA3AF]">({p.name})</span>
                    {p.connected ? (
                      <span className="inline-flex items-center rounded-full bg-[#156F5B]/10 px-2.5 py-0.5 text-[11px] font-heading font-semibold text-[#156F5B]">Connected</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-[11px] font-heading font-semibold text-[#9CA3AF]">Not connected</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-[#6B7280]">
                    {p.connected
                      ? `${p.site_count} site${p.site_count === 1 ? "" : "s"} synced${p.last_synced_at ? ` • last sync ${new Date(p.last_synced_at).toLocaleString()}` : ""}`
                      : "Connect to import this project's sites and points"}
                    {p.last_error && <span className="text-red-600"> • {p.last_error}</span>}
                  </div>
                </div>
              </div>
              {p.connected ? (
                <button
                  onClick={() => disconnect(p.name)}
                  disabled={busyName === p.name}
                  data-testid={`project-disconnect-btn-${i}`}
                  className="inline-flex items-center gap-2 rounded-md border border-[#E5E7EB] bg-white px-4 py-2 text-xs font-heading font-semibold text-[#4D4D4D] hover:border-red-300 hover:text-red-600 disabled:opacity-50"
                  style={{ transition: "color .2s ease, border-color .2s ease" }}
                >
                  <Unplug className="h-3.5 w-3.5" /> {busyName === p.name ? "Working…" : "Disconnect"}
                </button>
              ) : (
                <button
                  onClick={() => connect(p.name)}
                  disabled={busyName === p.name}
                  data-testid={`project-connect-btn-${i}`}
                  className="k-btn !py-2 !px-4 !text-xs"
                >
                  <Plug className="h-3.5 w-3.5" /> {busyName === p.name ? "Connecting…" : "Connect"}
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
